"use client";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef
} from "react";
import styled, { CSSProperties } from "styled-components";
import { focusEffect, shadow } from "../../../styles/globalsStyles";
import { defaultTheme } from "../../../styles/defaultTheme";
import Toggle from "react-toggle";
import { isBoolean } from "lodash";
export type SelectOptions = {
  label: string;
  value: string;
};
type InputType =
  | "text"
  | "file"
  | "password"
  | "number"
  | "checkbox"
  | "textarea"
  | "date"
  | "datetime-local"
  | "select"
  | "time"
  | "toggle";
export interface TextInputProps<T> {
  onChange?: (value: T) => void;
  value?: T;
  label?: string;
  maxWidth?: boolean;
  type?: InputType;
  direction?: "rtl" | "ltr";
  disabled?: boolean;
  onClickEnter?: () => void;
  accept?: string;
  placeholder?: string;
  regex?: RegExp;
  setIsError?: Dispatch<SetStateAction<boolean>>;
  isError?: boolean;
  selectOptions?: SelectOptions[];
  multipleFiles?: boolean;
  width?: CSSProperties["width"];
  isOneLineInput?: boolean;
}

export function TextInput<T>({
  onChange = () => {},
  value,
  label,
  maxWidth,
  type = "text",
  direction,
  disabled,
  accept,
  regex,
  placeholder,
  onClickEnter = () => {},
  isError = false,
  setIsError,
  selectOptions,
  multipleFiles,
  width,
  isOneLineInput
}: TextInputProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);

  const isInputTouched = useRef(false);
  const onInputChange = useCallback(
    (
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
      isInputTouched.current = true;
      if (type === "file") {
        if (multipleFiles) {
          // eslint-disable-next-line
          //@ts-ignore
          const file = e.target.files;

          onChange(file as T);
          return;
        }
        // eslint-disable-next-line
        //@ts-ignore
        const file = e.target.files?.[0];
        if (file) {
          onChange(file as T);
          return;
        }
      } else if (type === "checkbox") {
        const v = inputRef.current?.checked;

        onChange(v as T);
      } else if (type === "number") {
        onChange(e.target.value ? (+e.target.value as T) : ("" as T));
      } else if (type === "toggle") {
        if (!isBoolean(value)) {
          throw new Error("הערך לא בוליאני");
        }
        onChange(!value as T);
      } else {
        onChange(e.target.value as T);
      }
    },
    [multipleFiles, onChange, type, value]
  );

  useEffect(() => {
    // HOTFIX
    if (typeof value === "number") return;
    const stringifedValue =
      typeof value == "string"
        ? value
        : typeof value === "number"
        ? value.toString()
        : null;

    if (
      !regex ||
      stringifedValue === null ||
      stringifedValue === undefined ||
      !isInputTouched.current
    )
      return;

    setIsError?.(!regex.test(stringifedValue));
  }, [regex, setIsError, value]);

  const changedFirst = useRef(false);
  useEffect(() => {
    if (
      !changedFirst.current &&
      inputRef.current &&
      type === "checkbox" &&
      value
    ) {
      inputRef.current.checked = true;
      changedFirst.current = true;
    }
  }, [type, value]);

  return (
    <InputWrapper
      $maxWidth={maxWidth}
      $width={width}
      $inputType={type}
      $isOneLineInput={isOneLineInput}
    >
      {label && <Label>{label}</Label>}
      {type === "select" && selectOptions && (
        <StyledSelect
          value={value as string}
          onChange={onInputChange}
          $direction={direction}
          $isError={isError}
          disabled={disabled}
        >
          {selectOptions?.map((opt) => (
            <StyledSelectOption value={opt.value} key={opt.value}>
              {opt.label}
            </StyledSelectOption>
          ))}
        </StyledSelect>
      )}

      {type !== "textarea" && type !== "select" && type !== "toggle" && (
        <StyledTextInput
          type={type}
          onChange={onInputChange}
          onKeyDown={(e) => (e.key === "Enter" ? onClickEnter() : "")}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          value={type !== "checkbox" ? (value as any) : undefined}
          $direction={direction}
          $isError={isError}
          disabled={disabled}
          accept={accept}
          placeholder={placeholder}
          ref={inputRef}
          multiple={multipleFiles}
          // checked={type === "checkbox" ? value as boolean : undefined}
        />
      )}
      {type === "toggle" && (
        <Toggle
          id="cheese-status"
          defaultChecked={value as boolean}
          onChange={onInputChange}
        />
      )}

      {type === "textarea" && (
        <StyledTextArea
          onChange={onInputChange}
          onKeyDown={(e) => (e.key === "Enter" ? onClickEnter() : "")}
          value={value as string}
          placeholder={placeholder}
          disabled={disabled}
          $isError={isError}
        />
      )}
    </InputWrapper>
  );
}
export const Label = styled.span<{ $color?: CSSProperties["color"] }>`
  top: 8px;
  left: 12px;
  color: ${(p) => p.$color ?? defaultTheme.primaryColor};
  width: 100%;
`;

const StyledTextInput = styled.input<{
  $direction?: "rtl" | "ltr";
  $fileInput?: boolean;
  $isError?: boolean;
}>`
  border: 1px solid ${(p) => (p.$isError ? "red" : defaultTheme.secondaryColor)};
  color: ${defaultTheme.primaryColor};
  font-size: large;
  width: ${({ type }) => (type === "checkbox" ? "30px" : "100%")};
  height: ${({ type }) => (type === "checkbox" ? "30px" : "45px")};
  max-width: 100vw;
  outline: none;
  ${(p) => p.type !== "checkbox" && shadow}
  ${(p) => p.type !== "checkbox" && focusEffect}
    
  
  direction: ${({ $direction }) => $direction ?? "ltr"};
`;

const InputWrapper = styled.div<{
  $maxWidth?: boolean;
  $width?: CSSProperties["width"];
  $inputType: InputType;
  $isOneLineInput?: boolean;
}>`
  display: flex;
  flex-direction: ${({ $inputType, $isOneLineInput }) =>
    $isOneLineInput || $inputType === "checkbox" ? "row" : "column"};
  width: ${({ $maxWidth, $width }) => ($maxWidth ? "100%" : $width ?? "300px")};
  gap: 0.5rem;
  align-items: center;
`;

const StyledTextArea = styled.textarea<{ $isError?: boolean }>`
  border: 1px solid ${(p) => (p.$isError ? "red" : defaultTheme.secondaryColor)};
  color: ${defaultTheme.primaryColor};
  font-size: large;
  width: 100%;
  max-width: 100%;
  min-height: 100px;
  padding: 8px;
  outline: none;
  ${shadow}
  ${focusEffect}
`;

const StyledSelect = styled.select<{
  $direction?: "rtl" | "ltr";
  $fileInput?: boolean;
  $isError?: boolean;
}>`
  border: 1px solid ${(p) => (p.$isError ? "red" : defaultTheme.secondaryColor)};
  color: ${defaultTheme.primaryColor};
  font-size: large;
  width: 100%;
  height: 45px;
  max-width: 100vw;
  outline: none;
  ${shadow}
  ${focusEffect}
  direction: ${({ $direction }) => $direction ?? "ltr"};
`;
const StyledSelectOption = styled.option``;
